import { Card, Col, Empty, Row, Statistic, Tag, Typography } from 'antd';
import {
  BookOutlined, CalendarOutlined, ScheduleOutlined, TeamOutlined,
} from '@ant-design/icons';
import { Link } from 'react-router';
import { useAuthStore } from '@/store/auth';
import type { DashboardSummary } from '@/api/dashboard';
import { DashboardHeader, RecentNoticesCard, SignalRow } from './shared';

/**
 * Teacher dashboard — strictly classroom-relevant. No money, no defaulters,
 * no inventory, no admin signals. Just: my owned classes (from scope payload),
 * upcoming exams, today's attendance status, and links to mark/enter marks.
 */
export function TeacherDashboard({ data }: { data: DashboardSummary }) {
  const user = useAuthStore((s) => s.user);
  const scope = user?.scope;
  const myClasses = scope?.teacherClassSectionIds.length ?? 0;
  const mySubjects = scope?.teacherSubjectIds.length ?? 0;

  const attTotal = data.studentAttendanceToday.expected;
  const attPresentPct = attTotal === 0
    ? 0
    : Math.round((data.studentAttendanceToday.present / attTotal) * 1000) / 10;

  return (
    <div style={{ display: 'grid', gap: 20 }}>
      <DashboardHeader role={data.role} asOf={data.asOf} title="Classroom overview" />

      <Row gutter={[16, 16]}>
        <Col xs={24} sm={8}>
          <Card>
            <Statistic
              title="My class·sections"
              value={myClasses}
              prefix={<TeamOutlined />}
              styles={{ content: { fontSize: 22, fontWeight: 600 } }}
            />
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
              Across {mySubjects} subject{mySubjects === 1 ? '' : 's'}
            </Typography.Text>
          </Card>
        </Col>
        <Col xs={24} sm={8}>
          <Card>
            <Statistic
              title="Today's attendance (school-wide)"
              value={attTotal === 0 ? '—' : `${attPresentPct}%`}
              prefix={<CalendarOutlined />}
              styles={{ content: { fontSize: 22, fontWeight: 600 } }}
            />
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
              {attTotal === 0 ? 'Not marked yet' : `${data.studentAttendanceToday.present} of ${attTotal}`}
            </Typography.Text>
          </Card>
        </Col>
        <Col xs={24} sm={8}>
          <Card>
            <Statistic
              title="Upcoming exams (7 days)"
              value={data.academics.upcomingExamsThisWeek}
              prefix={<BookOutlined />}
              styles={{ content: { fontSize: 22, fontWeight: 600 } }}
            />
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
              Across all classes — yours included
            </Typography.Text>
          </Card>
        </Col>
      </Row>

      <Row gutter={[16, 16]}>
        <Col xs={24} md={16}>
          <Card title="Today" size="small">
            <ul style={{ margin: 0, paddingLeft: 18, lineHeight: 2 }}>
              <li><Link to="/attendance/daily"><ScheduleOutlined /> Mark today's attendance</Link></li>
              <li><Link to="/academics/marks"><BookOutlined /> Enter marks</Link></li>
              <li><Link to="/academics/timetable">View my timetable</Link></li>
              <li><Link to="/students">Find a student</Link></li>
            </ul>
            {myClasses === 0 && (
              <Empty
                style={{ marginTop: 16 }}
                description="You're not on the timetable yet. Ask the Principal or Admin to assign you."
              />
            )}
          </Card>
        </Col>
        <Col xs={24} md={8}>
          <Card title="My scope" size="small">
            <SignalRow icon={<TeamOutlined />} label="Sections I teach" value={myClasses.toString()} />
            <SignalRow icon={<BookOutlined />} label="Subjects I teach" value={mySubjects.toString()} />
            <div style={{ marginTop: 12 }}>
              <Tag color={myClasses > 0 ? 'success' : 'default'}>
                {myClasses > 0 ? 'Active in current year' : 'No assignments yet'}
              </Tag>
            </div>
          </Card>
        </Col>
      </Row>

      <RecentNoticesCard notices={data.recentNotices} />
    </div>
  );
}
